import { Nullable, Nullish } from '@tager/web-core';

import { PageModuleType } from '@/typings/common';
import DefaultTemplate from '@/modules/DefaultTemplate';
import ValueCar from '@/modules/ValueCar';
import Home from '@/modules/Home';
import EmergencyManager from '@/modules/EmergencyManager';
import SpecialOffer from '@/modules/SpecialOffer';
import BonusProgram from '@/modules/BonusProgram';
import AmpLanding from '@/modules/AmpLanding/AmpLanding';
import HowTo from '@/modules/HowTo';
import PersonalCabinet from '@/modules/PersonalCabinet';
import MobileAppLanding from '@/modules/MobileAppLanding';
import OnlineShopping from '@/modules/OnlineShopping';
import Insurance from '@/modules/Insurance';
import Brands from '@/modules/Brands';
import About from '@/modules/About';
import History from '@/modules/History';
import Leadership from '@/modules/Leadership';
import Dealers from '@/modules/Dealers';
import Subscription from '@/modules/Subscription';
import Atlantm30 from '@/modules/Atlantm30';
import Eco from '@/modules/Eco';
import Services from '@/modules/Services';
import HelpOnTheRoad from '@/modules/HelpOnTheRoad';
import CarModelLanding from '@/modules/CarModelLanding';
import CarModelCompareLanding from '@/modules/CarModelCompareLanding';
import TopLevelFunnelLanding from '@/modules/TopLevelFunnelLanding';
import HisunHome from '@/modules/HisunHome';
import JacHome from '@/modules/JacHome';
import SollersHome from '@/modules/SollersHome';
import JetourHome from '@/modules/JetourHome';
import B2bHome from '@/modules/B2bHome';
import ZeekrHome from '@/modules/ZeekrHome';
import ElectrocarsLanding from '@/modules/ElectrocarsLanding';
import SubdomainHome from '@/modules/SubdomainHome';
import Contacts from '@/modules/Contacts';
import TheLineup from '@/modules/TheLineup';
import VedaemLanding from '@/modules/VedaemLanding';
import AutoSelectionLanding from '@/modules/AutoSelectionLanding';
import ServiceBenefits from '@/modules/ServiceBenefits';
import ServiceValuationLanding from '@/modules/ServiceValuationLanding';
import FinanceLanding from '@/modules/FinanceLanding';
import TokensLanding from '@/modules/TokensLanding';
import { ServiceMain } from '@/modules/Service';
import { CreditLeasing, CreditLeasingSingle } from '@/modules/CreditLeasing';
import {
  getDealersThunk,
  getRateCarDataThunk,
} from '@/store/reducers/rate-car-data';
import {
  fetchHomeBigBannersThunk,
  fetchHomeSmallBannersThunk,
} from '@/store/reducers/banners';
import {
  fetchPostsListPostsThunk,
  fetchPostsListTagsThunk,
} from '@/store/reducers/posts-list';
import {
  fetchBrandsAndModelsThunk,
  fetchCatalogItemsThunk,
} from '@/store/reducers/catalog';
import {
  getServiceBrandsDataThunk,
  getServiceDealersDataThunk,
  getServicePostsDataThunk,
} from '@/store/reducers/service';
import { fetchAmpLandingDealersThunk } from '@/store/reducers/amp-landing';
import {
  getDetailedCarListThunk,
  getStockBrandsAndModelsThunk,
  getStockCarListThunk,
} from '@/store/reducers/cars-stock';
import { CarsStockFlag, CarsStockSort } from '@/services/stock/enums';
import { getMenuItemListThunk } from '@/store/reducers/tager/menus';
import { SelectedFiltersPayload } from '@/services/stock/typings';
import { fetchDealerReviewsThunk } from '@/store/reducers/single-dealer';
import { OffersTag } from '@/modules/FinanceLanding/types';
import VerifiedAtlantmLanding from '@/modules/VerifiedAtlantmLanding';
import EvaluationRedemption from '@/modules/EvaluationRedemption';
import RefitLanding from '@/modules/RefitLanding';
import RefitSingleLanding from '@/modules/RefitSingleLanding';

const DEFAULT_PAGE_MODULE: PageModuleType = {
  template: '',
  component: DefaultTemplate,
};

const PAGE_MODULE_LIST: Array<PageModuleType> = [
  {
    template: 'home',
    component: Home,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(fetchHomeBigBannersThunk()),
        store.dispatch(fetchHomeSmallBannersThunk()),
      ]);
    },
  },
  {
    template: 'value-car',
    component: ValueCar,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(getDealersThunk()),
        store.dispatch(getRateCarDataThunk()),
      ]);
    },
  },
  {
    template: 'amp-landing',
    component: AmpLanding,
    getInitialProps({ store }) {
      return store.dispatch(fetchAmpLandingDealersThunk());
    },
  },
  {
    template: 'special-offer',
    component: SpecialOffer,
    getInitialProps({ store }, templateData) {
      const stockCarsIds = templateData.templateFields
        ?.mayLikeDataStockNew as string[];
      const catalogCarsIds = templateData.templateFields
        ?.mayLikeDataCatalog as string[];

      return Promise.all([
        stockCarsIds &&
          stockCarsIds.length > 0 &&
          store.dispatch(
            getDetailedCarListThunk(
              stockCarsIds.map((id) => ({ carType: 'new', id: Number(id) }))
            )
          ),
        catalogCarsIds &&
          catalogCarsIds.length > 0 &&
          store.dispatch(fetchCatalogItemsThunk()),
      ]);
    },
  },
  { template: 'bonus-program', component: BonusProgram },
  { template: 'emergency-manager', component: EmergencyManager },
  { template: 'how-to', component: HowTo },
  { template: 'personal-cabinet', component: PersonalCabinet },
  { template: 'mobile-app', component: MobileAppLanding },
  { template: 'online-shopping', component: OnlineShopping },
  { template: 'insurance', component: Insurance },
  { template: 'brands', component: Brands },
  { template: 'about', component: About },
  { template: 'history', component: History },
  { template: 'leadership', component: Leadership },
  {
    template: 'dealers',
    component: Dealers,
  },
  {
    template: 'subscription',
    component: Subscription,
    getInitialProps({ store }) {
      return store.dispatch(getStockBrandsAndModelsThunk('all'));
    },
  },
  { template: 'atlantm30', component: Atlantm30 },
  { template: 'eco', component: Eco },
  {
    template: 'services',
    component: Services,
    getInitialProps({ store, query }, templateData) {
      const filteredTags = async () => {
        let tags: string[] = [];
        const allTagsArray = await store
          .dispatch(fetchPostsListTagsThunk('offers'))
          .then((response) => response?.map((item) => item.tag));
        tags = [
          ...templateData.templateFields?.sharesItems
            .map((item: { tag: string }) => item.tag)
            .filter((item: string) => allTagsArray?.includes(item)),
        ];
        if (tags.length) {
          return store.dispatch(
            fetchPostsListPostsThunk({ category: 'offers', tags })
          );
        }
      };

      return filteredTags();
    },
  },
  { template: 'help-on-the-road', component: HelpOnTheRoad },
  {
    template: 'service-main',
    component: ServiceMain,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(getServiceDealersDataThunk()),
        store.dispatch(getServiceBrandsDataThunk()),
        store.dispatch(getServicePostsDataThunk()),
      ]);
    },
  },
  { template: 'car-model-landing', component: CarModelLanding },
  { template: 'car-model-compare-landing', component: CarModelCompareLanding },
  { template: 'credit-leasing', component: CreditLeasing },
  { template: 'credit-leasing-single', component: CreditLeasingSingle },
  {
    template: 'electrocars-landing',
    component: ElectrocarsLanding,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(fetchCatalogItemsThunk()),
        store.dispatch(
          getStockCarListThunk(
            'electric',
            { limit: 6 },
            {
              filter: {
                flags: [CarsStockFlag.PartnerCar],
              },
            }
          )
        ),
      ]);
    },
  },
  {
    template: 'subdomain-home',
    component: SubdomainHome,
    getInitialProps({ store }) {
      return store.dispatch(getMenuItemListThunk('footer_purchase'));
    },
  },
  {
    template: 'top-level-funnel-landing',
    component: TopLevelFunnelLanding,
    getInitialProps({ store }) {
      return store.dispatch(fetchCatalogItemsThunk());
    },
  },
  {
    template: 'contacts',
    component: Contacts,
  },
  { template: 'the-lineup', component: TheLineup },
  { template: 'vedaem-landing', component: VedaemLanding },
  {
    template: 'auto-selection-landing',
    component: AutoSelectionLanding,
    getInitialProps({ store, query }, templateData) {
      const dealer = templateData.templateFields
        ?.reviewsDealer as Nullable<string>;

      if (!dealer) {
        return Promise.all([]);
      }

      return Promise.all([
        store.dispatch(fetchDealerReviewsThunk(Number(dealer))),
      ]);
    },
  },
  {
    template: 'service-benefits',
    component: ServiceBenefits,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(getServiceDealersDataThunk()),
        store.dispatch(getServiceBrandsDataThunk()),
      ]);
    },
  },
  {
    template: 'service-valuation-landing',
    component: ServiceValuationLanding,
    getInitialProps({ store }) {
      return Promise.all([
        store.dispatch(getServiceDealersDataThunk()),
        store.dispatch(getServiceBrandsDataThunk()),
      ]);
    },
  },
  {
    template: 'finance-landing',
    component: FinanceLanding,
    getInitialProps({ store }, templateData) {
      const offersTags = templateData.templateFields?.offersTags as OffersTag[];

      return Promise.all([
        store.dispatch(
          fetchPostsListPostsThunk({
            category: 'offers',
            tags:
              offersTags && offersTags.length !== 0
                ? offersTags.map((tag) => tag.name ?? '')
                : undefined,
          })
        ),
        store.dispatch(
          getStockCarListThunk(
            'new',
            { limit: 6, sort: CarsStockSort.RandomBrands },
            { filter: { flags: [CarsStockFlag.Credit] } }
          )
        ),
      ]);
    },
  },
  {
    template: 'tokens-landing',
    component: TokensLanding,
  },
  { template: 'hisun-home', component: HisunHome },
  { template: 'sollers-home', component: SollersHome },
  {
    template: 'jac-home',
    component: JacHome,
    getInitialProps({ store, query }, templateData) {
      const carsLimit = templateData.templateFields
        ?.stockItemsQuantity as number;
      const stockCarsBrandIds =
        (templateData.templateFields?.stockBrands as string[]) ?? [];

      if (!carsLimit || stockCarsBrandIds.length === 0) {
        return Promise.all([]);
      }

      const filters: SelectedFiltersPayload = {
        filter: {
          catalog: stockCarsBrandIds.map((brand) => ({
            brand: Number(brand),
          })),
        },
      };

      return Promise.all([
        store.dispatch(
          getStockCarListThunk('new', { limit: carsLimit }, filters)
        ),
      ]);
    },
  },
  {
    template: 'jetour-home',
    component: JetourHome,
  },
  {
    template: 'b2b-home',
    component: B2bHome,
    getInitialProps({ store, query }, templateData) {
      const carsLimit = templateData.templateFields
        ?.stockItemsQuantity as number;

      const stockCarsBrandIds =
        (templateData.templateFields?.stockBrands as string[]) ?? [];

      const stockCarsDealersIds =
        (templateData.templateFields?.stockDealers as string[]) ?? [];

      if (!carsLimit || stockCarsBrandIds.length === 0) {
        return Promise.all([]);
      }

      const filters: SelectedFiltersPayload = {
        filter: {
          catalog: stockCarsBrandIds.map((brand) => ({
            brand: Number(brand),
          })),
          dealers: stockCarsDealersIds.map((dealer) => Number(dealer)),
        },
      };

      return Promise.all([
        store.dispatch(
          getStockCarListThunk('new', { limit: carsLimit }, filters)
        ),
      ]);
    },
  },
  {
    template: 'zeekr-home',
    component: ZeekrHome,
  },
  {
    template: 'verified-atlantm-landing',
    component: VerifiedAtlantmLanding,
  },
  {
    template: 'evaluation-redemption-landing',
    component: EvaluationRedemption,
    getInitialProps({ store }) {
      return Promise.all([store.dispatch(fetchBrandsAndModelsThunk())]);
    },
  },
  {
    template: 'refit-landing',
    component: RefitLanding,
  },
  {
    template: 'refit-landing-single-page',
    component: RefitSingleLanding,
  },
];

export function getPageModuleByTemplate(
  template: Nullish<string>
): PageModuleType {
  return (
    PAGE_MODULE_LIST.find((pageModule) => pageModule.template === template) ??
    DEFAULT_PAGE_MODULE
  );
}
